import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { FiChevronDown, FiSearch, FiX } from "react-icons/fi";
import { PiInfoLight } from "react-icons/pi";
import { FaCheck, FaComments, FaHourglassHalf } from "react-icons/fa6";
import { Capacitor } from "@capacitor/core";
import { IoMdClose } from "react-icons/io";
import {
  getProjects,
  updateMilestoneStatus,
  updateProjectStatus,
  uploadMilestoneFile,
} from "../../Services/projectServices";
import { getFormattedNumber, toProperCase } from "../../Utils";
import { CREATE_PROJECT, VIEW_PROJECT } from "../../routes";
import {
  ACTION_LIST,
  FILTER_OPTIONS,
  LABELS,
  MILESTONE_ACTIONS,
  STATUS_CONFIG,
  TOAST_MESSAGES,
  STATUS,
  STATUS_ACTION_FILTERS,
  ACK_ACTION_FILTERS,
  STATUS_OPTIONS,
  ACTION_ROLE_MAP,
  STATUS_VALUE,
  PROJECT_TYPE_LABELS,
  PROJECT_TYPES,
} from "./constants";
import toast from "react-hot-toast";
import { SubMilestones, Pagination, ProjectDocuments } from "./Components";
import { RoleValidator } from "../../Components/Common";
import { ROLES } from "../../Constants/Strings";
import { useNotifications } from "../../Context/NotificationContext.js";
import { ChatWindow } from "../Messages/";
import { UserContext } from "../../Context/UserContext.js";

const ListProject = () => {
  const [expandedRow, setExpandedRow] = useState(null);
  const [statusFilter, setStatusFilter] = useState(FILTER_OPTIONS.STATUS[0]);
  const [typeFilter, setTypeFilter] = useState(FILTER_OPTIONS.TYPE[0]);
  const [statusOpen, setStatusOpen] = useState(false);
  const [desktopStatusOpen, setDesktopStatusOpen] = useState(false);
  const [typeOpen, setTypeOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [milestoneActions, setMilestoneActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const limit = 10;
  const navigate = useNavigate();
  const [rowActionsOpenFor, setRowActionsOpenFor] = useState(null);
  const [isSubMilestoneOpen, setIsSubMilestoneOpen] = useState(false);
  const [isMobileActionPopupOpen, setIsMobileActionPopupOpen] = useState(false);
  const actionDropdownRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [manualSelection, setManualSelection] = useState(false);
  const statusRef = useRef();
  const typeRef = useRef();
  const { fetchUnreadCount } = useNotifications();
  const [chatOpen, setChatOpen] = useState(false);
  const [chatProjectId, setChatProjectId] = useState(null);
  const [projectName, setProjectName] = useState("");
  const [showHandoverDocuments, setShowHandoverDocuments] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [search, setSearch] = useState("");
  const { user } = useContext(UserContext);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        statusRef.current &&
        !statusRef.current.contains(event.target) &&
        (statusOpen || desktopStatusOpen)
      ) {
        setStatusOpen(false);
        setDesktopStatusOpen(false);
      }
      if (
        typeRef.current &&
        !typeRef.current.contains(event.target) &&
        typeOpen
      ) {
        setTypeOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [statusOpen, desktopStatusOpen, typeOpen]);

  const fetchData = async (searchValue = search) => {
    try {
      setLoading(true);
      const offset = (page - 1) * limit;
      const res = await getProjects({
        limit,
        offset,
        status: statusFilter === "ALL" ? undefined : statusFilter,
        type: typeFilter === "ALL" ? undefined : typeFilter,
        search: searchValue || "",
      });

      if (res?.data) {
        setProjects(res.data.results);
        const totalCount = res.data.count || 0;
        setTotalPages(Math.ceil(totalCount / limit));
      } else {
        toast.error(TOAST_MESSAGES.PROJECT_FETCH_FAILED);
      }
    } catch (error) {
      toast.error(error?.message || "Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        actionDropdownRef.current &&
        !actionDropdownRef.current.contains(event.target)
      ) {
        setRowActionsOpenFor(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    fetchData();
  }, [page, statusFilter, typeFilter]);

  const toggleRow = (id, milestones) => {
    if (expandedRow === id) {
      setExpandedRow(null);
      setSelectedMilestone(null);
      setMilestoneActions([]);
      setRowActionsOpenFor(null);
    } else {
      setExpandedRow(id);

      // Default selection: first "in progress", else pending, else first milestone
      const inProgressMilestone = milestones.find(
        (m) => m.status === STATUS_VALUE.IN_PROGRESS
      );
      const pendingMilestone = milestones.find(
        (m) => m.status === STATUS_VALUE.PENDING
      );
      const targetMilestone =
        inProgressMilestone || pendingMilestone || milestones[0];
      setSelectedMilestone(targetMilestone);
      setMilestoneActions(getAvailableActions(targetMilestone));
    }
  };

  const toggleRowActionsDropdown = (rowId) => {
    setRowActionsOpenFor((prev) => (prev === rowId ? null : rowId));
  };

  const getStatusProps = (status) => {
    if (!status) return STATUS_CONFIG.default;
    const key = status.toLowerCase();
    return STATUS_CONFIG[key] || STATUS_CONFIG.default;
  };

  const handleMilestoneAction = (action) => {
    setIsMobileActionPopupOpen(false);
    if (!selectedMilestone) {
      toast.error(TOAST_MESSAGES.NO_MILESTONE_SELECTED);
      return;
    }

    if (action.type === ACTION_LIST.UPLOAD) {
      document.getElementById("milestone-upload").click();
    } else if (action.type === ACTION_LIST.IN_PROGRESS) {
      setManualSelection(false); // Reset manual selection flag when moving to In Progress, so effect can auto-shift later
      callMilestoneApi(action, { status: "IN_PROGRESS" });
    } else if (
      action.status === ACTION_LIST.COMPLETED &&
      action.type === ACTION_LIST.CUSTOMER_ACK
    ) {
      setManualSelection(false); // Reset manual selection flag when moving to In Progress, so effect can auto-shift later
      callMilestoneApi(action, { customer_acknowledgement: "COMPLETED" });
    } else if (action.type === ACTION_LIST.COMPLETED) {
      if (selectedMilestone.status !== "IN_PROGRESS") {
        toast.error(TOAST_MESSAGES.CANNOT_COMPLETE);
        return;
      }
      setManualSelection(false); // Reset manual selection when completing a milestone so effect will shift to next
      callMilestoneApi(action, { status: "COMPLETED" });
    } else if (action.type === ACTION_LIST.OPEN_POPUP) {
      setIsSubMilestoneOpen(true);
    } else if (action.type === ACTION_LIST.VIEW_DOCUMENTS) {
      setShowHandoverDocuments(true);
    } else if (action.type === ACTION_LIST.REDIRECT) {
      navigate(action.url.replace(":projectId", expandedRow));
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("milestone", selectedMilestone.id);

    try {
      setIsUploading(true);
      let res = await uploadMilestoneFile(expandedRow, formData);
      if (res.status === 201) {
        toast.success(TOAST_MESSAGES.FILE_UPLOADED_SUCCESFULLY);
      } else {
        toast.error(TOAST_MESSAGES.FILE_UPLOAD_FAILED);
      }
      fetchData();
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err.message ||
          TOAST_MESSAGES.FILE_UPLOAD_FAILED
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleChatClick = (id, ProjectName) => {
    setProjectName(ProjectName);
    setChatOpen(!chatOpen);
    setChatProjectId(id);
  };

  const callMilestoneApi = async (action, extraData) => {
    try {
      if (!selectedMilestone) {
        toast.error(TOAST_MESSAGES.NO_MILESTONE_SELECTED);
        return;
      }
      if (action.type === "upload") {
        let res = await uploadMilestoneFile(expandedRow, extraData);
        if (res.status === 201) {
          toast.success(TOAST_MESSAGES.FILE_UPLOADED_SUCCESFULLY);
        } else {
          toast.error(TOAST_MESSAGES.FILE_UPLOAD_FAILED);
        }
      } else {
        const res = await updateMilestoneStatus(
          expandedRow,
          selectedMilestone.id,
          extraData
        );
        if (res.status === 200) {
          setSelectedMilestone(res.data);
          await fetchData();
          setProjects((prevProjects) =>
            prevProjects.map((project) => {
              if (project.id === res.data.project) {
                return {
                  ...project,
                  milestones: project.milestones.map((m) =>
                    m.id === res.data.id ? res.data : m
                  ),
                };
              }
              return project;
            })
          );
        } else {
          toast.error(res?.error?.errors?.error?.[0] || res?.error.errors?.[0]);
        }
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err.message ||
          TOAST_MESSAGES.ACTION_FAILED
      );
    } finally {
      fetchUnreadCount();
      fetchData();
    }
  };

  const getAvailableActions = (milestone) => {
    if (!milestone) return [];

    let actions = MILESTONE_ACTIONS[milestone.title] || [];
    let statusFilters = STATUS_ACTION_FILTERS[milestone.status] || [];
    let ackFilters =
      ACK_ACTION_FILTERS[milestone.customer_acknowledgement] || [];

    return actions.filter((a) =>
      a.status
        ? !ackFilters.includes(a.status)
        : !statusFilters.includes(a.type)
    );
  };

  useEffect(() => {
    // Auto-select milestone after status completion only if it wasn't manually selected
    if (!expandedRow) return;

    const currProject = projects.find((p) => p.id === expandedRow);
    const currMilestones = currProject?.milestones || [];

    if (
      !manualSelection &&
      selectedMilestone?.status?.toUpperCase() === "COMPLETED"
    ) {
      // Find next milestone to select: in progress first, else pending
      const inProgress = currMilestones.find((m) => m.status === "IN_PROGRESS");
      const pending = currMilestones.find((m) => m.status === "PENDING");

      const target = inProgress || pending;

      if (target && target.id !== selectedMilestone?.id) {
        setSelectedMilestone(target);
      }

      // Reset manual selection flag for future auto-selections
      if (manualSelection) {
        setManualSelection(false);
      }
    }
  }, [projects, expandedRow, selectedMilestone?.id]);

  // Automatically update milestone actions when selected milestone changes
  useEffect(() => {
    if (selectedMilestone) {
      setMilestoneActions(getAvailableActions(selectedMilestone));
    }
  }, [selectedMilestone]);

  // close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      // if Status dropdown is open and click happens outside of it then close it
      if (statusRef.current && !statusRef.current.contains(e.target)) {
        setStatusOpen(false);
        setDesktopStatusOpen(false);
      }

      // if Type dropdown is open and click happens outside of it then close it
      if (typeRef.current && !typeRef.current.contains(e.target)) {
        setTypeOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside); // attach the listener on whole document
    return () => document.removeEventListener("mousedown", handleClickOutside); // cleanup the listener on unmount
  }, []);

  // const clearSearch = () => {
  //   setSearch("");
  //   setPage(1);
  //   fetchData(""); // ✅ explicitly send empty string
  //   setIsSearching(false);
  // };

  const handleSearch = () => {
    if (search.trim()) {
      setPage(1);
      fetchData(search.trim()); // ✅ explicitly pass current value
      setIsSearching(true);
    }
  };

  // 🟢 New State for Debounce Timer
  const [debounceTimer, setDebounceTimer] = useState(null);

  useEffect(() => {
    // Clear previous timer if user keeps typing
    if (debounceTimer) clearTimeout(debounceTimer);

    // Trigger API call only after 500ms of inactivity
    const timer = setTimeout(() => {
      setPage(1);
      fetchData(search.trim());
      setIsSearching(search.trim().length > 0);
    }, 500);

    setDebounceTimer(timer);

    return () => clearTimeout(timer);
  }, [search]); // 👈 Run when 'search' changes

  const clearSearch = () => {
    setSearch("");
    setPage(1);
    fetchData(""); // ✅ Immediately clear search
    setIsSearching(false);
  };

  return (
    <div className="m-2 h-[80vh]">
      <div className="flex justify-between text-left">
        <h1 className="text-3xl font-bold text-secondary px-4">Projects</h1>
        <div className="flex gap-4 items-center">
          <RoleValidator allowedRoles={[ROLES.ADMIN, ROLES.USER]} mode="hide">
            <button
              className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-secondary whitespace-nowrap rounded-md"
              onClick={() => {
                navigate(CREATE_PROJECT);
              }}
            >
              + Add Project
            </button>
          </RoleValidator>
        </div>
      </div>
      <div className="flex flex-col flex-wrap items-center justify-end gap-2 mt-2">
        <div className="flex w-full flex-row flex-wrap items-center md:justify-end gap-4 md:gap-9">
          <div className="flex gap-2 items-center justify-end">
            {/* Mobile View */}
            <div className="relative  z-40" ref={statusRef}>
              <button
                onClick={() => {
                  setStatusOpen((s) => !s);
                }}
                className="inline-flex items-center px-4 py-1 border rounded-md text-base font-medium text-secondary border-gray-300 hover:border-gray-500 focus:outline-none"
              >
                {LABELS.STATUS}
                <FiChevronDown className="w-5 h-5 ml-2" />
              </button>
              {statusOpen && (
                <div className="absolute left-0 z-10 mt-1 bg-white divide-y divide-gray-100 rounded-lg shadow w-28">
                  <ul className="py-2 text-base text-gray-700">
                    {FILTER_OPTIONS.STATUS.map((s) => (
                      <li key={s}>
                        <button
                          className="w-full px-4 py-2 text-left hover:bg-gray-100"
                          onClick={() => {
                            setStatusFilter(STATUS_VALUE[s]);
                            setPage(1);
                            setStatusOpen(false);
                          }}
                        >
                          {STATUS[s]}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="relative z-40" ref={typeRef}>
              <button
                onClick={() => {
                  setTypeOpen((t) => !t);
                }}
                className="inline-flex items-center px-4 py-1 border rounded-md text-base font-medium text-secondary border-gray-300 hover:border-gray-500 focus:outline-none"
              >
                {LABELS.TYPE}
                <FiChevronDown className="w-5 h-5 ml-2" />
              </button>
              {typeOpen && (
                <div className="absolute left-0 z-10 mt-1 bg-white divide-y divide-gray-100 rounded-lg shadow w-28">
                  <ul className="py-2 text-base text-gray-700">
                    {FILTER_OPTIONS.TYPE.map((t) => (
                      <li key={t}>
                        <button
                          className="w-full px-4 py-2 text-left hover:bg-gray-100"
                          onClick={() => {
                            setTypeFilter(t);
                            setPage(1);
                            setTypeOpen(false);
                          }}
                        >
                          {PROJECT_TYPE_LABELS[t]}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="w-1/2 flex items-center justify-end relative">
              <input
                type="text"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-36 md:w-44 pl-8 pr-8 py-2 rounded-lg border border-gray-300 shadow-sm
                focus:outline-none focus:ring-1 focus:ring-blue-500
                focus:border-blue-500 text-sm"
              />
              {search && (
                <button
                  onClick={clearSearch}
                  className="absolute top-2 right-2 text-gray-500 hover:text-blue-600 
                    bg-white text-sm font-medium transition-colors duration-200"
                >
                  <FiX size={20} />
                </button>
              )}
              {!search && (
                <FiSearch
                  size={20}
                  className="absolute top-2 right-2 text-gray-400 pointer-events-none"
                />
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-4">{LABELS.LOADING}</div>
        ) : projects.length > 0 ? (
          <>
            <div className="relative overflow-x-auto w-full border border-gray-200 rounded-lg shadow">
              <div className="h-[62vh] md:h-[58vh] overflow-y-auto">
                <table className="w-full text-md text-left text-gray-500 border-collapse">
                  <thead className="text-sm text-secondary uppercase border-b bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-6 py-3">{LABELS.EXPAND}</th>
                      <th className="px-6 py-3">{LABELS.PROJECT_NAME}</th>
                      <th className="px-6 py-3">{LABELS.CAPACITY}</th>
                      <th className="px-6 py-3 md:table-cell hidden">
                        {LABELS.TYPE_COL}
                      </th>
                      <th className="px-6 py-3 md:table-cell hidden">
                        {LABELS.CLIENT_NAME}
                      </th>
                      <th className="px-6 py-3">{LABELS.STATUS}</th>
                      <th className="px-6 py-3">{LABELS.ACTIONS}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((item) => (
                      <React.Fragment key={item.id}>
                        <tr
                          className="bg-white border-b hover:bg-gray-50 cursor-pointer odd:bg-gray-100 even:bg-white"
                          onClick={() => toggleRow(item.id, item.milestones)}
                        >
                          <td className="px-4 py-2 text-center">
                            <FiChevronDown
                              className={`w-5 h-5 transition-transform ${
                                expandedRow === item.id ? "rotate-180" : ""
                              }`}
                            />
                          </td>
                          <td className="px-6 py-3 font-medium text-gray-900 whitespace-nowrap max-w-[180px] md:max-w-[120px] truncate">
                            {item.name}
                          </td>
                          <td className="px-6 py-3 max-w-[60px]">
                            {getFormattedNumber(item.capacity, 0)}
                          </td>
                          <td className="px-6 py-2 max-w-[20px] whitespace-nowrap truncate md:table-cell hidden">
                            {item.type === PROJECT_TYPES[1].value
                              ? PROJECT_TYPES[1].label
                              : item.type}
                          </td>
                          <td className="px-6 py-3 max-w-[100px] whitespace-nowrap truncate md:table-cell hidden">
                            {item.client_name}
                          </td>
                          <td className="px-6 py-2 font-bold max-w-[150px] whitespace-nowrap  truncate">
                            {STATUS[item.status]}
                          </td>
                          <td className="px-5 py-2 max-w-[150px] overflow-hidden truncate whitespace-nowrap">
                            <div className="flex gap-6 items-center justify-start">
                              <button
                                title="Message"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleChatClick(item.id, item.name);
                                }}
                                className="text-primary cursor-pointer border border-primary rounded-full p-2"
                              >
                                <FaComments size={12} />
                              </button>
                              <PiInfoLight
                                className="text-primary cursor-pointer"
                                size={33}
                                onClick={() =>
                                  navigate(
                                    VIEW_PROJECT.replace(":projectId", item.id)
                                  )
                                }
                                title="View Details"
                              />
                            </div>
                          </td>
                        </tr>

                        {expandedRow === item.id && (
                          <tr className="bg-gray-50 overflow-x-auto w-full">
                            <td
                              colSpan="9"
                              className="px-2 py-4 relative w-full"
                            >
                              <div className={`w-full relative md:pr-1`}>
                                <div className="relative w-full">
                                  {isUploading && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-60 z-20">
                                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                                    </div>
                                  )}
                                  <div
                                    className={`flex justify-between items-center relative  w-full  my-6 overflow-x-auto -z-0`}
                                  >
                                    {item.milestones?.map(
                                      (milestone, index, arr) => {
                                        const { color, bg, icon, text } =
                                          getStatusProps(milestone.status);
                                        return (
                                          <div
                                            key={milestone.id}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setManualSelection(true); // when user clicks to select a milestone, set manual flag to true
                                              setSelectedMilestone(milestone);
                                              setMilestoneActions(
                                                getAvailableActions(milestone)
                                              );
                                              // ✅ If the logged-in user is a CUSTOMER
                                              if (
                                                user?.role === ROLES.CUSTOMER
                                              ) {
                                                // Customer can always see the mobile action popup for ALL milestones
                                                setIsMobileActionPopupOpen(
                                                  true
                                                );
                                              } else {
                                                // ✅ Admin / User logic
                                                if (
                                                  milestone?.title === "Start"
                                                ) {
                                                  if (
                                                    milestone?.status ===
                                                    "COMPLETED"
                                                  ) {
                                                    // Start is completed → do NOT open for Admin/User
                                                    setIsMobileActionPopupOpen(
                                                      false
                                                    );
                                                  } else {
                                                    // Start is NOT completed → open for Admin/User
                                                    setIsMobileActionPopupOpen(
                                                      true
                                                    );
                                                  }
                                                } else if (
                                                  milestone?.title ===
                                                    "Procurement" ||
                                                  milestone?.title ===
                                                    "Handover"
                                                ) {
                                                  setIsMobileActionPopupOpen(
                                                    true
                                                  );
                                                }
                                              }

                                              // ✅ Sub-milestone popup logic (unchanged)
                                              if (
                                                user?.role !== ROLES.CUSTOMER
                                              ) {
                                                if (
                                                  [
                                                    "Sanction Letter",
                                                    "Commission",
                                                    "Structure",
                                                    "Documentation",
                                                    "Electrical Installation",
                                                    "Meter Installation",
                                                  ].includes(milestone?.title)
                                                ) {
                                                  setIsSubMilestoneOpen(true);
                                                }
                                              }
                                            }}
                                            className={`flex flex-col h-max justify-between items-center text-center relative z-10 min-w-[110px] font-medium cursor-pointer gap-2 py-4 px-4 rounded-md ${
                                              selectedMilestone.id ===
                                              milestone.id
                                                ? "bg-gray-200"
                                                : ""
                                            }`}
                                            style={{ flex: 1 }}
                                          >
                                            {index > 0 && (
                                              <div
                                                className={`absolute top-8 left-0 w-1/2 h-1 -z-10 ${
                                                  getStatusProps(
                                                    arr[index - 1].status
                                                  ).color
                                                }`}
                                              />
                                            )}
                                            <div
                                              className={`w-10 h-10 rounded-full flex items-center justify-center ${color}`}
                                            >
                                              {icon}
                                            </div>
                                            {index < arr.length - 1 && (
                                              <div
                                                className={`absolute top-8 right-0 w-1/2 h-1 ${color} -z-10`}
                                              />
                                            )}
                                            <div className="h-[20px]  mt-2 text-sm w-full">
                                              {milestone.title}{" "}
                                            </div>
                                            <div
                                              className={`mt-3 text-[12px] px-2 py-0.5 rounded-full ${bg}`}
                                            >
                                              {text}
                                            </div>
                                            <div>
                                              {(() => {
                                                const {
                                                  color,
                                                  bg,
                                                  icon,
                                                  text,
                                                } = getStatusProps(
                                                  milestone.customer_acknowledgement
                                                );
                                                return (
                                                  <div
                                                    className={`h-[40px] mt-1 text-[12px] px-2 py-0.5 rounded-full ${
                                                      milestone.customer_acknowledgement ===
                                                      ACTION_LIST.PENDING
                                                        ? "text-gray-400"
                                                        : "text-green-400"
                                                    }`}
                                                  >
                                                    {milestone.customer_acknowledgement ===
                                                    ACTION_LIST.PENDING
                                                      ? "Client Acknowledgment Pending"
                                                      : "Acknowledged"}
                                                  </div>
                                                );
                                              })()}
                                            </div>
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                </div>
                              </div>

                              <input
                                id="milestone-upload"
                                type="file"
                                style={{ display: "none" }}
                                onChange={handleFileChange}
                              />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="w-full flex justify-end">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={(p) => setPage(p)}
              />
            </div>
          </>
        ) : (
          <div className="text-center py-4">{LABELS.NO_PROJECTS}</div>
        )}
        {isSubMilestoneOpen && (
          <SubMilestones
            selectedMilestone={selectedMilestone}
            setIsSubMilestoneOpen={setIsSubMilestoneOpen}
            projectId={expandedRow}
            fetchMilestoneData={fetchData}
            updateCurrentMilestoneStatus={updateMilestoneStatus}
            setSelectedMilestone={setSelectedMilestone}
            setProjects={setProjects}
          />
        )}

        {showHandoverDocuments && (
          <ProjectDocuments
            onClose={() => {
              setShowHandoverDocuments(false);
            }}
            showDocumentsTable={true}
            showDocumentUpload={false}
            showProjectDocuments={true}
            showHandover={true}
            project_Id={expandedRow}
            currentMilestoneStatus={selectedMilestone?.status}
          />
        )}
        <div>
          {isMobileActionPopupOpen && selectedMilestone && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="flex flex-col gap-4 bg-white w-full max-w-md p-4 m-2 rounded-lg">
                {/* Header */}
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg font-bold">
                    {selectedMilestone.title} - Actions
                  </h2>
                  <button
                    onClick={() => setIsMobileActionPopupOpen(false)}
                    className="text-red-500 hover:text-gray-800"
                  >
                    <IoMdClose size={20} />
                  </button>
                </div>

                {/* Actions */}
                {selectedMilestone && milestoneActions.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {milestoneActions.map((action, idx) => {
                      console.log("mieskndjknc =>", action);
                      const allowCustomer = action?.type === "VIEW_DOCUMENTS";
                      const { color, bg, icon, text } = getStatusProps(
                        action.type
                      );
                      return (
                        <RoleValidator
                          key={idx}
                          allowedRoles={
                            ACTION_ROLE_MAP[action.type] || [
                              ROLES.USER,
                              ROLES.ADMIN,
                              allowCustomer ? ROLES.CUSTOMER : "",
                            ]
                          }
                        >
                          <button
                            className={`flex-1 min-w-[100px] truncate text-center py-2 text-sm hover:bg-gray-100 px-2 ${bg} rounded-md`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMilestoneAction(action);
                              setRowActionsOpenFor(null);
                            }}
                          >
                            <div className="flex gap-4 items-center justify-center">
                              <div className="flex items-center">
                                {action.icon}
                              </div>
                              <div className="text-center">{action.label}</div>
                            </div>
                          </button>
                        </RoleValidator>
                      );
                    })}
                  </div>
                ) : (
                  <div className="px-4 py-2 text-sm text-gray-400">
                    No actions for this milestone
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {chatOpen && (
        <ChatWindow
          projectId={chatProjectId}
          onClose={() => setChatOpen(false)}
          projectName={projectName}
        />
      )}
    </div>
  );
};

export default ListProject;
